import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import MuiTextField, { type TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import { forwardRef } from 'react';

import { InfoIconButton } from '../InfoIconButton';

export type TextFieldProps = Omit<MuiTextFieldProps, 'label'> & {
  infoTooltip?: string;
  loading?: boolean;
  label?: string;
};

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(({ infoTooltip, loading, ...rest }, ref) => {
  const inputSlotProps = loading
    ? {
        ...(typeof rest.slotProps?.input === 'object' ? rest.slotProps.input : {}),
        endAdornment: (
          <InputAdornment position="end">
            <CircularProgress size={rest.size === 'small' ? 16 : 20} />
          </InputAdornment>
        ),
      }
    : rest.slotProps?.input;

  const callerHtmlInput = rest.slotProps?.htmlInput;
  const isCallerHtmlInputCallback = typeof callerHtmlInput === 'function';
  const callerHtmlInputProps =
    !isCallerHtmlInputCallback && callerHtmlInput != null ? (callerHtmlInput as Record<string, unknown>) : {};
  const shouldDefaultInputMode =
    rest.type === 'number' && !isCallerHtmlInputCallback && callerHtmlInputProps.inputMode == null;
  const htmlInputProps = shouldDefaultInputMode
    ? { inputMode: 'decimal' as const, ...callerHtmlInputProps }
    : callerHtmlInput;

  const slotProps = {
    ...rest.slotProps,
    htmlInput: htmlInputProps,
    input: inputSlotProps,
  };

  return infoTooltip ? (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        width: rest.fullWidth ? '100%' : undefined,
      }}
    >
      <MuiTextField {...rest} ref={ref} slotProps={slotProps} />
      <InfoIconButton tooltip={infoTooltip} sx={{ marginTop: 1 }} />
    </Box>
  ) : (
    <MuiTextField {...rest} ref={ref} slotProps={slotProps} />
  );
});

TextField.displayName = 'TextField';
